import {
  download,
  RESULT_HEADERS,
  resultRows,
  resultsFilename,
  type ResultsSource,
} from './resultsFile.js';

/**
 * Story #47 — results as a file someone else can open.
 *
 * Built from the payload the page already holds, so there is no export
 * endpoint: the server has nothing to add that the results view is not already
 * showing, and a route that re-derived it would be a second answer to the same
 * question, free to disagree with the first.
 *
 * The shape, the headers, the row walk and the filename moved to
 * `resultsFile.ts` when the workbook (#79) arrived and needed the same ones.
 * What stayed here is what does not port: a CSV is a text file a spreadsheet
 * guesses at, and everything below is an answer to that.
 */
export type CsvSource = ResultsSource;

/**
 * Characters a spreadsheet reads as the start of a formula rather than as text.
 *
 * A leading hyphen is on the list even though it is ordinary in written
 * English ("- none of these"), because `-2+3` is a formula and no rule short
 * of parsing tells the two apart. The cost is a visible apostrophe on a few
 * honest options; the alternative is that opening a colleague's exported poll
 * can run their arithmetic — or their `=HYPERLINK`.
 */
const FORMULA_STARTERS = ['=', '+', '-', '@', '\t', '\r'];

/** Escaped rather than typed literally: the character is invisible in a diff. */
export const BOM = '\uFEFF';

const CSV_MIME = 'text/csv;charset=utf-8';

/**
 * One CSV field: neutralised, then quoted if it needs to be.
 *
 * Quoting alone is not the defence against formula injection — Excel evaluates
 * a quoted `=1+1` just as happily — so the apostrophe goes on first and the
 * quoting is only RFC 4180's escaping.
 */
function field(value: string | number): string {
  let text = String(value);
  if (FORMULA_STARTERS.some((starter) => text.startsWith(starter))) text = `'${text}`;

  // Leading and trailing spaces are quoted too, so a round-trip through a
  // spreadsheet gives back the option text the author actually typed.
  if (/[",\r\n]/.test(text) || text !== text.trim()) {
    return `"${text.replaceAll('"', '""')}"`;
  }
  return text;
}

/**
 * One row per option, which is the grain the numbers live at: a reader can
 * pivot rows up to a question or a screen, and could not split them back down.
 *
 * CRLF line endings and a UTF-8 byte-order mark, both for Excel: without the
 * mark it reads a poll written in anything but ASCII as mojibake.
 */
export function resultsToCsv(source: CsvSource): string {
  const rows = resultRows(source).map(({ runDate, screenNumber, question, option }) =>
    [
      field(runDate),
      field(screenNumber),
      field(question.text),
      field(option.text),
      field(option.count),
      field(option.percentage),
    ].join(','),
  );

  return `${BOM}${[RESULT_HEADERS.join(','), ...rows].join('\r\n')}\r\n`;
}

/** "Kickoff poll" → "kickoff-poll-results.csv", plus the date for one run. */
export function csvFilename(source: CsvSource): string {
  return resultsFilename(source, 'csv');
}

/**
 * Hands the file to the browser.
 *
 * The charset is declared on the blob as well as written as a `BOM`: the mark
 * is what Excel reads, and the MIME type is what everything else does.
 */
export function downloadCsv(filename: string, csv: string): void {
  download(filename, new Blob([csv], { type: CSV_MIME }));
}
